<template>
    <div class="forum-container">
        <div class="forum-header">
            <div class="header-content">
                <h1>数学学习论坛</h1>
                <p>提问、分享、共同进步</p>
            </div>
            <el-button type="primary" size="large" @click="$router.push('/forum/create')" icon="Plus">
                发布新帖
            </el-button>
        </div>

        <div class="forum-filters">
            <el-tabs v-model="activeTab" @tab-change="handleTabChange">
                <el-tab-pane label="全部" name="all"></el-tab-pane>
                <el-tab-pane label="新鲜事" name="moment"></el-tab-pane>
                <el-tab-pane label="提问" name="question"></el-tab-pane>
                <el-tab-pane label="经验分享" name="article"></el-tab-pane>
                <el-tab-pane label="打卡" name="checkin"></el-tab-pane>
            </el-tabs>

            <div class="search-bar">
                <el-input v-model="searchQuery" placeholder="搜索帖子..." class="search-input" @keyup.enter="handleSearch">
                    <template #append>
                        <el-button icon="Search" @click="handleSearch" />
                    </template>
                </el-input>
            </div>
        </div>

        <div v-loading="loading" class="post-list">
            <el-empty v-if="posts.length === 0 && !loading" description="暂无帖子" />

            <div v-for="post in posts" :key="post.id" class="post-card" @click="goToDetail(post)">
                <div class="post-main">
                    <!-- Left Avatar area -->
                    <div class="post-avatar-container" @click.stop="goToUserSpace(post.author.id)"
                        style="cursor: pointer;">
                        <el-avatar :size="40" :src="post.author.avatar || defaultAvatar" />
                    </div>

                    <!-- Right Main Content area -->
                    <div class="post-content-container">
                        <div class="post-header-row">
                            <div class="header-left">
                                <el-tag class="post-type-tag"
                                    :type="post.type === 'question' ? 'warning' : (post.type === 'moment' ? 'primary' : 'success')"
                                    effect="plain" size="small">
                                    {{ post.type === 'question' ? '提问' : (post.type === 'moment' ? '新鲜事' : (post.type
                                        ===
                                        'checkin' ? '每日打卡' : '分享')) }}
                                </el-tag>
                                <el-tag v-if="post.status === 'resolved'" class="post-status-tag" type="success"
                                    effect="dark" size="small">
                                    已解决
                                </el-tag>
                                <h3 class="post-title" v-if="post.type !== 'moment' && post.type !== 'checkin'">{{
                                    post.title }}
                                </h3>
                                <div class="checkin-info" v-if="post.type === 'checkin' && post.node">
                                    正在学习：
                                    <el-tag v-if="post.activity" size="small" type="success" effect="light"
                                        class="activity-tag clickable-tag" @click.stop="goToActivity(post.activity.id)">
                                        {{ post.activity.title }}
                                    </el-tag>
                                    <span class="connector" v-if="post.activity">-</span>
                                    <el-tag size="small" effect="plain" class="node-tag">{{ post.node.title }}</el-tag>
                                </div>
                            </div>

                            <!-- Date & Views placed at the very end of header -->
                            <div class="header-right">
                                <span class="post-date" v-if="post.ipLocation">IP属地: {{ post.ipLocation }}</span>
                                <span class="post-date">{{ formatDate(post.createdAt) }}</span>
                                <span class="stats-item">
                                    <el-icon>
                                        <View />
                                    </el-icon> {{ post.views }}
                                </span>
                                <span class="stats-item" v-if="post.type === 'question'">
                                    <el-icon>
                                        <ChatDotRound />
                                    </el-icon> {{ post.commentCount || 0 }}
                                </span>
                            </div>
                        </div>

                        <!-- Content Brief without indentation now -->
                        <div class="post-content-brief">
                            <p class="post-excerpt" v-if="post.type !== 'moment'">{{ getExcerpt(post.content) }}</p>
                            <div v-else-if="post.type === 'moment' || post.type === 'checkin'">
                                <div class="post-excerpt moment-content">
                                    <v-md-editor :model-value="formatMomentContent(post.content, post.type)"
                                        mode="preview" />
                                </div>

                                <!-- Image Grid for Moments and Check-ins -->
                                <div class="moment-image-grid" v-if="getImages(post).length > 0">
                                    <div v-for="(img, imgIndex) in getImages(post)" :key="imgIndex"
                                        class="moment-image-wrapper"
                                        :class="{ 'single-image': getImages(post).length === 1 }" @click.stop>
                                        <el-image :src="img" :preview-src-list="getImages(post)"
                                            :initial-index="imgIndex" fit="cover" class="moment-image"
                                            hide-on-click-modal preview-teleported />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Lazy Loading Sentinel -->
            <div class="load-more-section" v-if="posts.length > 0">
                <div v-if="hasMore" ref="sentinelRef" class="loading-trigger">
                    <el-icon v-if="loadingMore" class="is-loading" :size="24">
                        <Loading />
                    </el-icon>
                    <span v-if="loadingMore">正在加载更多...</span>
                    <span v-else>继续滚动加载</span>
                </div>
                <div v-else class="no-more">
                    <el-divider>没有更多内容了</el-divider>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick, watch } from 'vue';
import { useRouter } from 'vue-router';
import api from '@/utils/api';
import dayjs from 'dayjs';
import { Loading } from '@element-plus/icons-vue';

const router = useRouter();
const activeTab = ref('all');
const searchQuery = ref('');
const loading = ref(false);
const loadingMore = ref(false);
const hasMore = ref(true);
const posts = ref([]);
const total = ref(0);
const currentPage = ref(1);
const pageSize = ref(10);
const defaultAvatar = 'https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png';
const sentinelRef = ref(null);
let observer = null;

const fetchPosts = async (isLoadMore = false) => {
    if (isLoadMore && loadingMore.value) return;

    if (isLoadMore) {
        loadingMore.value = true;
    } else {
        loading.value = true;
        currentPage.value = 1;
        posts.value = [];
        hasMore.value = true;
    }

    try {
        let newPosts = [];
        let newTotal = 0;

        if (activeTab.value === 'checkin') {
            const params = {
                page: currentPage.value,
                page_size: pageSize.value
            };
            const response = await api.get('/api/checkins', { params });
            newPosts = (response.data.checkIns || []).map(c => ({
                ...c,
                author: c.user,
                type: 'checkin',
                views: 0
            }));
            newTotal = response.data.total;
        } else if (activeTab.value === 'all') {
            const forumParams = {
                page: currentPage.value,
                page_size: pageSize.value,
                type: ''
            };
            const checkinParams = {
                page: currentPage.value,
                page_size: pageSize.value
            };

            const [forumRes, checkinRes] = await Promise.all([
                api.get('/api/forum/posts', { params: forumParams }),
                api.get('/api/checkins', { params: checkinParams })
            ]);

            const forumPosts = (forumRes.data.posts || []).map(p => ({
                ...p,
                unifiedCreatedAt: new Date(p.createdAt).getTime()
            }));

            const checkinPosts = (checkinRes.data.checkIns || []).map(c => ({
                ...c,
                author: c.user,
                type: 'checkin',
                views: 0,
                unifiedCreatedAt: c.createdAt * 1000
            }));

            newPosts = [...forumPosts, ...checkinPosts].sort((a, b) => b.unifiedCreatedAt - a.unifiedCreatedAt);
            newTotal = Math.max(forumRes.data.total || 0, checkinRes.data.total || 0);
        } else {
            const params = {
                page: currentPage.value,
                page_size: pageSize.value,
                type: activeTab.value,
            };
            if (searchQuery.value) {
                params.keyword = searchQuery.value;
            }
            const response = await api.get('/api/forum/posts', { params });
            newPosts = response.data.posts || [];
            newTotal = response.data.total;
        }

        total.value = newTotal;
        if (isLoadMore) {
            posts.value = [...posts.value, ...newPosts];
        } else {
            posts.value = newPosts;
        }

        // Determine if there are more posts to load
        hasMore.value = posts.value.length < newTotal && newPosts.length > 0;
    } catch (error) {
        console.error('Failed to fetch data:', error);
    } finally {
        loading.value = false;
        loadingMore.value = false;
    }
};

const loadMore = () => {
    if (hasMore.value && !loadingMore.value && !loading.value) {
        currentPage.value += 1;
        fetchPosts(true);
    }
};

const handleTabChange = () => {
    currentPage.value = 1;
    fetchPosts();
};

const handleSearch = () => {
    currentPage.value = 1;
    fetchPosts();
};

// Setup IntersectionObserver for infinite scroll
const setupObserver = () => {
    if (observer) observer.disconnect();
    nextTick(() => {
        if (!sentinelRef.value) return;
        observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    loadMore();
                }
            });
        }, { threshold: 0.1 });
        observer.observe(sentinelRef.value);
    });
};

// Re-setup observer when sentinel appears/disappears
watch(sentinelRef, (el) => {
    if (el) setupObserver();
});

const goToActivity = (id) => {
    if (!id) return;
    router.push(`/activities/${id}`);
};

const goToUserSpace = (id) => {
    if (!id) return;
    router.push(`/user/${id}`);
};

const goToDetail = (item) => {
    if (item.type === 'checkin') {
        router.push(`/checkins/${item.id}`);
    } else {
        router.push(`/forum/${item.id}`);
    }
};

const getExcerpt = (content) => {
    if (!content) return '';
    // Remove markdown symbols for excerpt
    const stripped = content.replace(/[#*`]/g, '');
    return stripped.length > 100 ? stripped.substring(0, 100) + '...' : stripped;
};

const formatMomentContent = (content, type) => {
    if (!content) return '';
    if (type === 'checkin') return content;
    // Strip image urls from markdown so they don't show up in the text
    let textOnly = content.replace(/!\[.*?\]\((.*?)\)/g, '');
    // Replace hashtags that are preceded by space or start of line
    return textOnly.replace(/(^|\s)(#\S+)/g, '$1<span style="color: #409eff;">$2</span>');
};

const getImages = (post) => {
    if (post.type === 'checkin') return post.imageUrls || [];
    return extractMomentImages(post.content);
};

const extractMomentImages = (content) => {
    if (!content) return [];
    // Only match markdown image syntax with http/https urls
    const imageRegex = /!\[.*?\]\((https?:\/\/[^\s]+)\)/g;
    const images = [];
    let match;
    while ((match = imageRegex.exec(content)) !== null) {
        images.push(match[1]); // match[1] contains the URL part inside the parentheses
    }
    return images;
};

const formatDate = (date) => {
    return dayjs(date).format('YYYY-MM-DD HH:mm');
};

onMounted(() => {
    fetchPosts();
});

onUnmounted(() => {
    if (observer) observer.disconnect();
});
</script>

<style scoped>
.forum-container {
    max-width: 1000px;
    margin: 0 auto;
    padding: 20px;
}

.forum-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 30px;
}

.header-content h1 {
    margin: 0;
    font-size: 28px;
    color: #303133;
}

.header-content p {
    margin: 5px 0 0;
    color: #909399;
}

.forum-filters {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 20px;
    border-bottom: 1px solid #ebeef5;
}

.search-bar {
    width: 300px;
    margin-top: 5px;
}

.post-list {
    min-height: 400px;
}

.post-card {
    background: #fff;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 15px;
    cursor: pointer;
    transition: all 0.3s ease;
    border: 1px solid #ebeef5;
}

.post-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
    border-color: #409eff;
}

.post-main {
    display: flex;
    gap: 16px;
    margin-bottom: 20px;
}

.post-avatar-container {
    flex-shrink: 0;
}

.post-content-container {
    flex: 1;
    min-width: 0;
}

.post-header-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 8px;
}

.header-left {
    display: flex;
    align-items: center;
    gap: 10px;
}

.post-status-tag {
    flex-shrink: 0;
}

.header-right {
    display: flex;
    align-items: center;
    gap: 16px;
    color: #909399;
    font-size: 15px;
    /* Increased from 13px */
    flex-shrink: 0;
}

.stats-item {
    display: flex;
    align-items: center;
    gap: 6px;
    /* Increased spacing slightly */
}

.post-title {
    margin: 0;
    font-size: 18px;
    color: #303133;
    font-weight: 600;
}

.post-content-brief {
    margin: 0;
}

.post-excerpt {
    margin: 0;
    color: #909399;
    font-size: 14px;
    line-height: 1.6;
}

.moment-content {
    color: #303133;
    font-size: 15px;
    margin-top: 8px;
    margin-bottom: 12px;
}

.activity-tag {
    margin: 0 4px;
}

.clickable-tag {
    cursor: pointer;
    transition: all 0.2s ease;
}

.clickable-tag:hover {
    opacity: 0.8;
    transform: scale(1.05);
}

.connector {
    color: #909399;
    margin: 0 4px;
}

.node-tag {
    margin: 0 4px;
}

:deep(.moment-content .v-md-editor-preview) {
    padding: 0 !important;
    margin: 0 !important;
}

:deep(.moment-content .v-md-editor-preview > div) {
    margin: 0 !important;
    padding: 0 !important;
}

:deep(.moment-content .github-markdown-body),
:deep(.moment-content .vuepress-markdown-body) {
    padding: 0 !important;
    margin: 0 !important;
    color: inherit;
    line-height: 1.6;
}

:deep(.moment-content .github-markdown-body p),
:deep(.moment-content .vuepress-markdown-body p) {
    margin: 0 !important;
    line-height: inherit;
}

.moment-image-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    margin-top: 10px;
    margin-bottom: 5px;
    max-width: 620px;
}

.moment-image-wrapper {
    width: 200px;
    height: 200px;
    overflow: hidden;
    border-radius: 4px;
    background-color: #f5f7fa;
}

.moment-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.post-meta {
    display: none;
}

.meta-center {
    display: flex;
    justify-content: center;
}

.meta-right {
    display: flex;
    gap: 20px;
}

.meta-right span {
    display: flex;
    align-items: center;
    gap: 4px;
}

.load-more-section {
    margin-top: 30px;
    display: flex;
    justify-content: center;
}

.loading-trigger {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 20px;
    color: #909399;
    font-size: 14px;
}

.no-more {
    width: 100%;
    color: #c0c4cc;
    font-size: 13px;
}

@media screen and (max-width: 768px) {
    .forum-container {
        padding: 15px;
    }

    .forum-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 15px;
    }

    .forum-header .el-button {
        width: 100%;
    }

    .forum-filters {
        flex-direction: column;
        align-items: stretch;
        gap: 10px;
    }

    .search-bar {
        width: 100%;
        margin-top: 0;
        margin-bottom: 10px;
    }

    .post-card {
        padding: 15px;
    }

    .post-main {
        flex-direction: column;
        gap: 10px;
    }

    .post-title {
        font-size: 16px;
    }

    .post-meta {
        flex-wrap: wrap;
        gap: 10px;
    }
}
</style>
